import React, { useState } from 'react'
import Plot from 'react-plotly.js'

import { makeStyles } from '@material-ui/core/styles'
import { CircularProgress } from '@material-ui/core'

import {
    AnalyticsLoadingState,
    ServiceRequestCommonIssues,
    ServiceRequestDetailAnalytics,
} from '../../hooks/useEZFolioWorkorderAnalytics'
import ChartToggleButton from './ChartToggleButton'
import { Container } from '../Container'

const useStyles = makeStyles({
    analyticsDashboard: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px',
        padding: '20px',
        height: 'calc(100vh - 120px)',
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    chartContainer: {
        flex: '1 1 calc(50% - 24px)',
        minWidth: '400px',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        height: '500px',
        '& h3': {
            marginTop: 0,
            marginBottom: '16px',
        },
        '& .js-plotly-plot': {
            flex: 1,
        },
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        '& th, & td': {
            padding: '12px',
            textAlign: 'left',
            borderBottom: '1px solid #eee',
        },
        '& th': {
            backgroundColor: '#f5f5f5',
            fontWeight: 600,
            position: 'sticky',
            top: 0,
        },
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '8px',
    },
    viewButton: {
        textTransform: 'none',
        marginLeft: '8px',
    },
    chartHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        '& h3': {
            margin: 0,
        },
    },
})

interface ServiceRequestChartsProps {
    loadingState: AnalyticsLoadingState
    analytics: ServiceRequestDetailAnalytics
    commonIssues: ServiceRequestCommonIssues[]
}

const ServiceRequestCharts: React.FC<ServiceRequestChartsProps> = ({
    analytics,
    commonIssues,
    loadingState,
}) => {
    const classes = useStyles()
    const [showTimeGraphs, setShowTimeGraphs] = useState({
        services: false,
        tenants: false,
        locations: false,
    })

    // Transform and sort data for services bar chart
    const servicesData = (() => {
        const sortedServices = Object.entries(analytics.top_services)
            .map(([name, data]) => ({
                name,
                count: data.reduce((sum, { count }) => sum + count, 0),
            }))
            .sort((a, b) => b.count - a.count)

        return {
            x: sortedServices.map((item) => item.count),
            y: sortedServices.map((item) => item.name),
            type: 'bar' as const,
            orientation: 'h' as const,
            marker: {
                color: '#1f77b4',
                opacity: 0.8,
            },
            name: 'Services',
        }
    })()

    // Transform and sort data for locations pie chart
    const locationsData = (() => {
        const sortedLocations = Object.entries(analytics.top_locations)
            .map(([name, data]) => ({
                name,
                count: data.reduce((sum, { count }) => sum + count, 0),
            }))
            .sort((a, b) => b.count - a.count)

        return {
            values: sortedLocations.map((item) => item.count),
            labels: sortedLocations.map((item) => item.name),
            type: 'pie' as const,
            hole: 0.4,
            name: 'Locations',
        }
    })()

    // Replace the existing issues treemap data with this bar chart data
    const issuesData = {
        x: commonIssues.map(({ count }) => count),
        y: commonIssues.map(({ issue }) => issue),
        type: 'bar' as const,
        orientation: 'h' as const,
        marker: {
            color: '#2ecc71',
            opacity: 0.8,
        },
        name: 'Issues',
    }

    // Transform vendor data for comparison chart
    const vendorComparisonData = (() => {
        // Sort vendors by assigned count
        const sortedVendors = Object.entries(analytics.vendor_data)
            .sort(([, a], [, b]) => b.assigned_count - a.assigned_count)
            .map(([vendor]) => vendor)

        return [
            {
                x: sortedVendors,
                y: sortedVendors.map(
                    (vendor) => analytics.vendor_data[vendor].assigned_count,
                ),
                name: 'Assigned',
                type: 'bar' as const,
                marker: { color: '#17a2b8' },
            },
            {
                x: sortedVendors,
                y: sortedVendors.map(
                    (vendor) => analytics.vendor_data[vendor].completed_count,
                ),
                name: 'Completed',
                type: 'bar' as const,
                marker: { color: '#28a745' },
            },
        ]
    })()

    // Transform data for daily trends
    const dailyTrendsData = [
        {
            x: analytics.assigned_daily.map((item) => item.date),
            y: analytics.assigned_daily.map((item) => item.count),
            type: 'scatter' as const,
            mode: 'lines+markers' as const,
            name: 'Assigned',
            line: { color: '#17a2b8' },
            marker: { size: 6 },
        },
        {
            x: analytics.completed_daily.map((item) => item.date),
            y: analytics.completed_daily.map((item) => item.count),
            type: 'scatter' as const,
            mode: 'lines+markers' as const,
            name: 'Completed',
            line: { color: '#28a745' },
            marker: { size: 6 },
        },
    ]

    // Transform data for tenants lollipop chart
    const tenantsData = (() => {
        const sortedTenants = Object.entries(analytics.top_tenants)
            .map(([name, data]) => ({
                name,
                count: data.reduce((sum, { count }) => sum + count, 0),
            }))
            .sort((a, b) => b.count - a.count)

        return [
            {
                x: sortedTenants.map((item) => item.count),
                y: sortedTenants.map((item) => item.name),
                type: 'scatter' as const,
                mode: 'lines' as const,
                line: {
                    color: '#9c27b0',
                    width: 2,
                },
                name: 'Tenants',
            },
            {
                x: sortedTenants.map((item) => item.count),
                y: sortedTenants.map((item) => item.name),
                type: 'scatter' as const,
                mode: 'markers' as const,
                marker: {
                    color: '#9c27b0',
                    size: 12,
                    symbol: 'circle',
                },
                name: 'Tenants',
            },
        ]
    })()

    // Update getTimeSeriesData to maintain the sorted order
    const getTimeSeriesData = (data: {
        [key: string]: { date: string; count: number }[]
    }) => {
        const allDates = new Set<string>()
        Object.values(data).forEach((series) =>
            series.forEach((point) => allDates.add(point.date)),
        )

        // Sort the entries by total count before creating the time series
        const sortedEntries = Object.entries(data)
            .map(([name, series]) => ({
                name,
                total: series.reduce((sum, { count }) => sum + count, 0),
                series,
            }))
            .sort((a, b) => b.total - a.total)

        return sortedEntries.map(({ name, series }) => ({
            x: series.map((point) => point.date),
            y: series.map((point) => point.count),
            type: 'scatter' as const,
            mode: 'lines+markers' as const,
            name: name,
        }))
    }

    const toggleTimeGraph = (type: 'services' | 'tenants' | 'locations') => {
        setShowTimeGraphs((prev) => ({
            ...prev,
            [type]: !prev[type],
        }))
    }

    return (
        <div className={classes.analyticsDashboard}>
            {/* Services Section */}
            <div className={classes.chartContainer}>
                <div className={classes.chartHeader}>
                    <h3>Top Services</h3>
                    <ChartToggleButton
                        showTrend={showTimeGraphs.services}
                        onChange={() => toggleTimeGraph('services')}
                    />
                </div>
                <Plot
                    data={
                        showTimeGraphs.services
                            ? getTimeSeriesData(analytics.top_services)
                            : [servicesData]
                    }
                    layout={{
                        height: 400,
                        margin: {
                            l: 50,
                            r: 40,
                            t: 20,
                            b: showTimeGraphs.services ? 80 : 40,
                        },
                        yaxis: {
                            title: showTimeGraphs.services
                                ? { text: 'Number of Requests' }
                                : { text: 'Service Type' },
                            automargin: true,
                        },
                        xaxis: showTimeGraphs.services
                            ? {
                                  title: { text: 'Date', standoff: 30 },
                                  type: 'date',
                                  tickangle: -45,
                                  automargin: true,
                                  tickfont: { size: 10 },
                              }
                            : {
                                  title: { text: 'Number of Requests' },
                              },
                        showlegend: showTimeGraphs.services,
                        legend: showTimeGraphs.services
                            ? {
                                  orientation: 'h',
                                  y: -0.3,
                              }
                            : undefined,
                    }}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>

            {/* Locations Section */}
            <div className={classes.chartContainer}>
                <div className={classes.chartHeader}>
                    <h3>Top Request Locations</h3>
                    <ChartToggleButton
                        showTrend={showTimeGraphs.locations}
                        onChange={() => toggleTimeGraph('locations')}
                    />
                </div>
                <Plot
                    data={
                        showTimeGraphs.locations
                            ? getTimeSeriesData(analytics.top_locations)
                            : [locationsData]
                    }
                    layout={{
                        height: 400,
                        margin: {
                            l: showTimeGraphs.locations ? 50 : 20,
                            r: 20,
                            t: 20,
                            b: showTimeGraphs.locations ? 80 : 20,
                        },
                        showlegend: true,
                        legend: {
                            orientation: 'h',
                            y: showTimeGraphs.locations ? -0.3 : -0.1,
                        },
                        yaxis: showTimeGraphs.locations
                            ? {
                                  title: { text: 'Number of Requests' },
                                  automargin: true,
                              }
                            : {},
                        xaxis: showTimeGraphs.locations
                            ? {
                                  title: { text: 'Date', standoff: 30 },
                                  type: 'date',
                                  tickangle: -45,
                                  automargin: true,
                                  tickfont: { size: 10 },
                              }
                            : {},
                    }}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>

            {/* Top Tenants Section */}
            <div className={classes.chartContainer}>
                <div className={classes.chartHeader}>
                    <h3>Most Requested Tenants</h3>
                    <ChartToggleButton
                        showTrend={showTimeGraphs.tenants}
                        onChange={() => toggleTimeGraph('tenants')}
                    />
                </div>
                <Plot
                    data={
                        showTimeGraphs.tenants
                            ? getTimeSeriesData(analytics.top_tenants)
                            : tenantsData
                    }
                    layout={{
                        height: 400,
                        margin: {
                            l: 50,
                            r: 40,
                            t: 20,
                            b: showTimeGraphs.tenants ? 80 : 40,
                        },
                        yaxis: {
                            title: showTimeGraphs.tenants
                                ? { text: 'Number of Requests' }
                                : { text: 'Tenant' },
                            automargin: true,
                        },
                        xaxis: showTimeGraphs.tenants
                            ? {
                                  title: { text: 'Date', standoff: 30 },
                                  type: 'date',
                                  tickangle: -45,
                                  automargin: true,
                                  tickfont: { size: 10 },
                              }
                            : {
                                  title: { text: 'Number of Requests' },
                              },
                        showlegend: showTimeGraphs.tenants,
                        legend: showTimeGraphs.tenants
                            ? {
                                  orientation: 'h',
                                  y: -0.3,
                              }
                            : undefined,
                    }}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>

            {/* Vendor Performance Comparison */}
            <div className={classes.chartContainer}>
                <h3>Vendor Assignments</h3>
                <Plot
                    data={vendorComparisonData}
                    layout={{
                        height: 400,
                        margin: { l: 50, r: 20, t: 20, b: 150 },
                        barmode: 'group',
                        xaxis: {
                            tickangle: -45,
                            title: {
                                text: 'Vendor',
                                standoff: 30,
                            },
                            automargin: true,
                            tickfont: { size: 10 },
                            ticks: '',
                            showticksuffix: 'all',
                            ticksuffix: '  ',
                        },
                        yaxis: {
                            title: {
                                text: 'Number of Requests',
                                standoff: 20,
                            },
                        },
                        legend: {
                            orientation: 'h',
                            y: -0.4,
                        },
                    }}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>

            {/* Daily Trends Chart */}
            <div className={classes.chartContainer}>
                <h3>Daily Request Trends</h3>
                <Plot
                    data={dailyTrendsData}
                    layout={{
                        height: 400,
                        margin: { l: 50, r: 20, t: 20, b: 80 },
                        xaxis: {
                            title: {
                                text: 'Date',
                                standoff: 30,
                            },
                            tickangle: -45,
                            type: 'date',
                            tickformat: '%Y-%m-%d',
                            automargin: true,
                            tickfont: { size: 10 },
                            ticks: '',
                            showticksuffix: 'all',
                            ticksuffix: '  ',
                        },
                        yaxis: {
                            title: {
                                text: 'Number of Requests',
                                standoff: 20,
                            },
                            rangemode: 'tozero',
                        },
                        legend: {
                            orientation: 'h',
                            y: -0.3,
                        },
                        hovermode: 'x unified',
                    }}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>

            {/* Common Issues Distribution */}
            <div className={classes.chartContainer}>
                <div className={classes.chartHeader}>
                    <h3>Common Issues Distribution</h3>
                </div>
                {loadingState.commonIssues ? (
                    <Container
                        style={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            display: 'flex',
                            height: '100%',
                        }}
                    >
                        <CircularProgress size={40} />
                    </Container>
                ) : (
                    <Plot
                        data={[issuesData]}
                        layout={{
                            height: 400,
                            margin: { l: 50, r: 40, t: 20, b: 40 },
                            yaxis: {
                                automargin: true,
                                title: {
                                    text: 'Issue Type',
                                    standoff: 10,
                                },
                                tickfont: { size: 11 },
                                ticks: '',
                                showticksuffix: 'all',
                                ticksuffix: '  ',
                            },
                            xaxis: {
                                title: {
                                    text: 'Number of Occurrences',
                                    standoff: 20,
                                },
                            },
                            showlegend: false,
                        }}
                        config={{ responsive: true }}
                        style={{ width: '100%' }}
                    />
                )}
            </div>
        </div>
    )
}

export default ServiceRequestCharts
